import { isStoryOfUsLine, resolveLineRole } from './lineRole';
import { CART_COPY, CATEGORIES, CATEGORY_ORDER, JOURNEY_NAMES } from './cartCopy';

/**
 * @typedef {Object} CartChildLine  One gift wrap child, as cart-packaging-row.liquid writes it.
 * @property {string} key
 * @property {number} variantId
 * @property {string} [variantTitle]
 * @property {string} [handle]
 * @property {string} [title]
 * @property {number} price        final_line_price, minor units
 * @property {number|null} [plainPrice]  price of the product's first non-name variant, minor units
 * @property {Record<string, any>} [properties]
 *
 * @typedef {{ text: string, surcharge: number|null, ownLine?: boolean }} DetailPart  ownLine: shown on a line of its own
 * @typedef {{ key: string, name: string, price: number, details: DetailPart[] }} BlockItem
 * @typedef {{ category: string, label: string, items: BlockItem[] }} BlockPanel
 * @typedef {{ count: number, subtotal: number, categoryLabel: string, summary: string, panels: BlockPanel[] }} PackagingBlock
 */

const TEXT_KEYS = Object.keys(CART_COPY.textLabels);
// String checks, not a regex: the build's comment stripper reads the "//" in `\/uploads\//` as a comment.
const isUpload = (value) => ['/cdn.shopify.com/', '/uploads/'].some((part) => String(value ?? '').includes(part));

/** "Yes: Philippe" -> "Philippe"; "Yes: Card Message: Hi" -> "Hi" (the cart writer's prefixes). */
const stripPrefixes = (value) =>
  String(value ?? '')
    .replace(/^\s*Yes:\s*/i, '')
    .replace(/^Card Message:\s*/i, '')
    .trim();

/**
 * The seal's letter from the bag or box line's "Add Ons" ("Wax Stamp, Initial: P"), else from the
 * seal line's own variant title ("Initial: P" or "P").
 * @param {string|undefined|null} source
 * @returns {string}
 */
export function sealValue(source) {
  const text = String(source ?? '');
  const at = text.lastIndexOf('Initial:');
  return (at === -1 ? text : text.slice(at + 'Initial:'.length)).trim();
}

/** "Wax seal, initial P" for a letter, "Wax seal, Doodle Heart" for a design. */
const sealText = (value) => (value.length === 1 ? `${CART_COPY.seal}, initial ${value}` : `${CART_COPY.seal}, ${value}`);

const positive = (amount) => (Number.isFinite(amount) && amount > 0 ? amount : null);

/** Category labels joined the way the ticket writes them: "BOX", "BOX & CARD", "BOX, GIFT BAG & CARD". */
const joinWords = (words) =>
  words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} & ${words[words.length - 1]}`;

/**
 * Builds the packaging block for one wrapped piece from its child lines (one _giftwrap_key).
 * The Story of Us card and envelope share the key but are not packaging (ALBAU-909), so they are left out.
 * Pure: no DOM, no fetch. Prices stay in minor units; the view formats them.
 * @param {{ lines: CartChildLine[] }} args
 * @returns {PackagingBlock}
 */
export function buildPackagingBlock({ lines: childLines = [] }) {
  const lines = childLines.filter((line) => !isStoryOfUsLine(line));
  const roles = lines.map((line) => ({ line, role: resolveLineRole(line) }));
  const seals = roles.filter((entry) => entry.role === 'stamp').map((entry) => entry.line);
  const attached = new Set();
  const hasRole = (role) => roles.some((entry) => entry.role === role);
  const hasBag = hasRole('bag');
  const hasBox = hasRole('box');

  const sealsOf = (line) =>
    seals.filter((seal) => {
      const match = String(seal.properties?._giftwrap_stamp) === String(line.variantId);
      if (match) attached.add(seal.key);
      return match;
    });

  /** @returns {BlockItem} */
  const toItem = (line, role, category) => {
    const properties = line.properties || {};
    const details = [];
    if (role === 'luxe') details.push({ text: CART_COPY.luxeDetail, surcharge: null });

    const name = properties.Personalisation ? stripPrefixes(properties.Personalisation) : '';
    if (name) {
      const surcharge = line.plainPrice == null ? null : positive(line.price - line.plainPrice);
      const text = role === 'keepsake' ? `Name on the lid, "${name}"` : `Name, "${name}"`;
      details.push({ text, surcharge });
    }

    const lineSeals = sealsOf(line);
    const sealLabel = sealValue(properties['Add Ons'] || lineSeals[0]?.variantTitle);
    if (sealLabel && (properties['Add Ons'] || lineSeals.length)) {
      const sealPrice = lineSeals.reduce((sum, seal) => sum + (Number(seal.price) || 0), 0);
      details.push({ text: sealText(sealLabel), surcharge: positive(sealPrice) });
    }

    if (role === 'card' || role === 'insert') {
      // Every typed text (ALBAU-900): one stays unlabelled and inline; two or more are labelled, one per line.
      const texts = TEXT_KEYS.filter((key) => !isUpload(properties[key]))
        .map((key) => ({ key, value: stripPrefixes(properties[key]) }))
        .filter(({ value }) => value);
      for (const { key, value } of texts) {
        if (texts.length === 1) details.push({ text: `‘${value}’`, surcharge: null });
        else details.push({ text: `${CART_COPY.textLabels[key]}: ‘${value}’`, surcharge: null, ownLine: true });
      }
      if (Object.keys(properties).some((key) => key.startsWith('UV') && isUpload(properties[key]))) {
        details.push({ text: CART_COPY.photo, surcharge: null });
      }
    }

    const personalised = Boolean(name || lineSeals.length || properties['Add Ons']);
    let itemName = line.title || '';
    if (role === 'luxe' || role === 'signature' || role === 'keepsake') itemName = JOURNEY_NAMES[role];
    if (role === 'bag' && category === 'giftBag' && personalised) itemName = JOURNEY_NAMES.personalisedBag;

    const price = (Number(line.price) || 0) + lineSeals.reduce((sum, seal) => sum + (Number(seal.price) || 0), 0);
    return { key: line.key, name: itemName, price, details };
  };

  const categoryOf = (role) => {
    if (role === 'luxe' || role === 'signature') return 'box';
    if (role === 'box') return hasBag ? 'giftSet' : 'box';
    if (role === 'bag') return hasBox ? 'giftSet' : 'giftBag';
    if (role === 'keepsake' || role === 'insert' || role === 'card' || role === 'other') return role;
    return null;
  };

  /** @type {Record<string, BlockItem[]>} */
  const buckets = {};
  const add = (category, item) => {
    if (!buckets[category]) buckets[category] = [];
    buckets[category].push(item);
  };

  // Bags before boxes so the gift set reads "bag, then box".
  const ordered = [...roles].sort((a, b) => Number(a.role === 'box') - Number(b.role === 'box'));
  for (const { line, role } of ordered) {
    const category = categoryOf(role);
    if (category) add(category, toItem(line, role, category));
  }
  for (const seal of seals) {
    if (attached.has(seal.key)) continue;
    const value = sealValue(seal.variantTitle);
    add('seal', {
      key: seal.key,
      name: CART_COPY.seal,
      price: Number(seal.price) || 0,
      details: value ? [{ text: sealText(value), surcharge: null }] : []
    });
  }

  const panels = CATEGORY_ORDER.filter((category) => buckets[category]).map((category) => ({
    category,
    label: CATEGORIES[category].label,
    items: buckets[category]
  }));

  const summary = panels
    .flatMap((panel) => (panel.category === 'giftSet' ? [JOURNEY_NAMES.pair] : panel.items.map((item) => item.name)))
    .join(' · ');

  return {
    count: panels.length,
    subtotal: lines.reduce((sum, line) => sum + (Number(line.price) || 0), 0),
    categoryLabel: joinWords(panels.map((panel) => CATEGORIES[panel.category].word)),
    summary,
    panels
  };
}
